import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import './ParkingLocationsModal.css';
import {
  X,
  MapPin,
  Clock,
  MessageCircle,
  Navigation,
  Share2,
  Car,
  CircleDollarSign,
  Footprints,
  Hourglass,
  Star,
} from 'lucide-react';
import { useScrollToTopOnChange } from '../utils/scrollUtils';
import AIChatbot from './AIChatbot';

interface ParkingLocation {
  id?: string;
  name: string;
  latitude: number;
  longitude: number;
  distance_m?: number;
  walk_time_min?: number;
  cost?: string;
  type?: string;
  busyness_level?: string;
  search_time?: string;
  notes?: string;
  booking_url?: string;
  pros?: string[];
  cons?: string[];
  recommended_by?: string | null;
  value?: string;
}

interface ParkingLocationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: ParkingLocation | null;
}

const ParkingLocationsModal: React.FC<ParkingLocationsModalProps> = ({ isOpen, onClose, location }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'community' | 'chat'>('overview');

  const headerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Measure header and tabs height for content positioning
  useEffect(() => {
    const updateHeights = () => {
      if (headerRef.current && tabsRef.current && modalRef.current) {
        const headerHeight = headerRef.current.offsetHeight;
        const tabsHeight = tabsRef.current.offsetHeight;
        modalRef.current.style.setProperty('--modal-header-height', `${headerHeight}px`);
        modalRef.current.style.setProperty('--modal-tabs-height', `${tabsHeight}px`);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, [location?.name]);

  // Use scroll-to-top utility hook with automatic scrolling on tab changes
  const { scrollToTop } = useScrollToTopOnChange([activeTab]);

  if (!isOpen || !location) return null;

  const getBusynessColor = (level?: string): string => {
    switch (level?.toLowerCase()) {
      case 'low':
        return '#10b981';
      case 'moderate':
        return '#f59e0b';
      case 'high':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `Check out ${location.name} parking on VibeCheck!`,
        text: `I found this parking spot at ${location.name}`,
        url: window.location.href,
      }).catch((error) => {
        // Silently handle share cancellation or errors
        if (error.name !== 'AbortError') {
          console.log('Share error:', error);
        }
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        // Could show a toast notification here
      });
    }
  };

  const getDirections = () => {
    if (location.latitude && location.longitude) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`,
        '_blank'
      );
    }
  };

  const renderOverviewTab = () => {
    return (
      <div className="parking-overview">
        {/* Header Info Card */}
        <div className="parking-info-card">
          <div className="parking-card-header">
            <span className={`parking-type-badge ${location.type?.toLowerCase().includes('street') ? 'street' : 'carpark'}`}>
              {location.type || 'Parking'}
            </span>
            {location.busyness_level && (
              <div className="parking-status-badge" style={{ '--status-color': getBusynessColor(location.busyness_level) } as React.CSSProperties}>
                <Clock size={14} />
                <span>{location.busyness_level}</span>
              </div>
            )}
          </div>
          <h2 className="parking-name">{location.name}</h2>
          
          {/* Key Metrics */}
          <div className="parking-metrics">
            {location.distance_m && (
              <div className="parking-metric">
                <MapPin size={16} />
                <span>{location.distance_m}m</span>
              </div>
            )}
            {location.walk_time_min && (
              <div className="parking-metric">
                <Footprints size={16} />
                <span>{location.walk_time_min} min walk</span>
              </div>
            )}
            {location.search_time && (
              <div className="parking-metric">
                <Hourglass size={16} />
                <span>{location.search_time}</span>
              </div>
            )}
          </div>
        </div>

        {/* Cost Card */}
        {location.cost && (
          <div className="parking-info-card">
            <div className="parking-card-title">
              <h3>Cost</h3>
            </div>
            <p className="parking-card-text">{location.cost}</p>
          </div>
        )}

        {/* Notes Card */}
        {location.notes && (
          <div className="parking-info-card">
            <div className="parking-card-title">
              <h3>Notes</h3>
            </div>
            <p className="parking-card-text">{location.notes}</p>
          </div>
        )}

        {/* Pros Card */}
        {location.pros && location.pros.length > 0 && (
          <div className="parking-info-card pros-card">
            <div className="parking-card-title">
              <h3>Pros</h3>
            </div>
            <ul className="parking-list">
              {location.pros.map((pro, index) => (
                <li key={index}>{pro}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Cons Card */}
        {location.cons && location.cons.length > 0 && (
          <div className="parking-info-card cons-card">
            <div className="parking-card-title">
              <h3>Cons</h3>
            </div>
            <ul className="parking-list">
              {location.cons.map((con, index) => (
                <li key={index}>{con}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Recommendation Card */}
        {(location.recommended_by || location.value) && (
          <div className="parking-info-card recommendation-card">
            {location.recommended_by && (
              <div className="parking-recommendation-item">
                <Star size={16} />
                <span><strong>Recommended by:</strong> {location.recommended_by}</span>
              </div>
            )}
            {location.value && (
              <div className="parking-recommendation-item">
                <span><strong>Value:</strong> {location.value}</span>
              </div>
            )}
          </div>
        )}

        {/* Book Now Button */}
        {location.booking_url && (
          <a 
            href={location.booking_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="parking-directions-button"
          >
            <Car size={20} />
            Book now
          </a>
        )}

        {/* Get Directions Button */}
        {location.latitude && location.longitude && (
          <button 
            onClick={getDirections}
            className="parking-directions-button"
          >
            <Navigation size={20} />
            Get directions
          </button>
        )}
      </div>
    );
  };

  const renderCommunityTab = () => {
    return (
      <div className="community-section">
        <div className="tab-header">
          <h2>Community Reviews</h2>
          <p>See what others are saying about this parking location</p>
        </div>

        <div className="empty-state">
          <MessageCircle size={48} />
          <h3>No reviews yet</h3>
          <p>Be the first to share your experience at this parking location!</p>
        </div>
      </div>
    );
  };

  const modalContent = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={onClose}
    >
      <motion.div
        ref={modalRef}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div ref={headerRef} className="modal-header fullscreen">
          <div className="header-left">
            <h1 className="location-title">{location.name}</h1>
          </div>
          <div className="header-right">
            {/* Favorites button hidden for now */}
            {/* <button onClick={() => {}} className="icon-action-button" title="Save">
              <Heart size={18} />
            </button> */}
            <button onClick={handleShare} className="icon-action-button" title="Share">
              <Share2 size={18} />
            </button>
            <button onClick={onClose} className="icon-action-button close" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div ref={tabsRef} className="modal-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: Car },
            { id: 'community', label: 'Community', icon: MessageCircle },
            { id: 'chat', label: 'AI Chat', icon: MessageCircle },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-tab={id}
              onClick={() => {
                console.log('🔍 Tab clicked:', id);
                setActiveTab(id as any);
              }}
              className={`tab-button ${activeTab === id ? 'active' : ''}`}
            >
              <Icon size={18} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <motion.div
          className="modal-content fullscreen"
        >
          <AnimatePresence mode="wait">
            {activeTab === 'overview' && (
              <motion.div
                key="overview"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                {renderOverviewTab()}
              </motion.div>
            )}

            {activeTab === 'community' && (
              <motion.div
                key="community"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                {renderCommunityTab()}
              </motion.div>
            )}

            {activeTab === 'chat' && (
              <motion.div
                key="chat"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="tab-content"
              >
                <AIChatbot 
                  location={{
                    location: location.name,
                    latitude: location.latitude,
                    longitude: location.longitude,
                    busyness: 50,
                    vibeRating: 3.5,
                  }} 
                />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    </motion.div>
  );

  return createPortal(modalContent, document.body);
};

export default ParkingLocationsModal;

